import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import "./index.sass";
import { FuturesPairs } from "@components/pages/Futures/components/Quotes/FuturesPairs";
import { formatNumberWithCommas } from "@helpers/formatNumber";
import { useTranslation } from "@helpers/translate";
import clockIcon from "@assets/images/icons/clock.svg";
import { formatPrice } from "@helpers/formatPrice";
import { switchFuturesPair } from "./switchFuturesPair";
import { FavoritePairsShortcuts } from "./favorites/FavoritePairsShortcuts";
import {
  resolvePositionMarkPrice,
  sumDisplayedPositionPnl,
} from "@helpers/formatOrders";
import RangeHighArrowIcon from "@assets/icons/RangeHighArrowIcon";
import RangeLowArrowIcon from "@assets/icons/RangeLowArrowIcon";

const toFinitePrice = (value) => {
  if (value == null || value === "") return null;

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatQuotePrice = (value) => {
  const numericValue = toFinitePrice(value);

  if (numericValue == null) return "—";

  return `$${formatPrice(numericValue)}`;
};

const formatCompactPrice = (value) => {
  const numericValue = toFinitePrice(value);

  if (numericValue == null) return "—";

  if (numericValue >= 1000) {
    const inK = numericValue / 1000;
    const formatted = inK.toFixed(1).replace(/\.0$/, "");
    return `$${formatted}k`;
  }

  return formatQuotePrice(numericValue);
};

const formatOpenPnl = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return "$0.00";

  const formatted = Math.abs(numericValue).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (numericValue > 0) return `+$${formatted}`;
  if (numericValue < 0) return `-$${formatted}`;

  return `$${formatted}`;
};

const formatChangePercent = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return "—";

  const formatted = formatNumberWithCommas(numericValue);

  return numericValue > 0 ? `+${formatted}%` : `${formatted}%`;
};

const RangeArrow = ({ direction }) =>
  direction === "high" ? (
    <RangeHighArrowIcon className="futures-quote__range-arrow futures-quote__range-arrow--high" />
  ) : (
    <RangeLowArrowIcon className="futures-quote__range-arrow futures-quote__range-arrow--low" />
  );

const RangeStat = ({ direction, label, value }) => (
  <div className="futures-quote__range">
    <p className="futures-quote__range-label">{label}</p>
    <p className="futures-quote__range-price">
      <RangeArrow direction={direction} />
      <span className="futures-quote__range-value futures-quote__range-value--full">
        {formatQuotePrice(value)}
      </span>
      <span className="futures-quote__range-value futures-quote__range-value--compact">
        {formatCompactPrice(value)}
      </span>
    </p>
  </div>
);

export const Quotes = ({
  onCloseAllPositions,
  closeAllDisabled = true,
}) => {
  const {
    chartPrice,
    tickersLoading,
    tickerData,
    pair,
    price,
    markPrice,
    tickersMap,
    openPositions,
    positionMarkPrices,
  } = useSelector((state) => state.futures);
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const { __ } = useTranslation();
  const openPnl = useMemo(
    () =>
      sumDisplayedPositionPnl(openPositions, (order) =>
        resolvePositionMarkPrice(order, {
          markPrices: positionMarkPrices,
          pairSlug: pair?.slug,
          activePairMarkPrice: markPrice,
          lastPrice: price ?? tickerData?.lastPrice ?? chartPrice,
          tickersMap,
        }),
      ),
    [
      openPositions,
      positionMarkPrices,
      pair?.slug,
      markPrice,
      price,
      tickerData?.lastPrice,
      chartPrice,
      tickersMap,
    ],
  );

  const currentPrice =
    toFinitePrice(chartPrice) ?? toFinitePrice(tickerData?.lastPrice);
  const changeValue = Number(tickerData?.priceChangePercent);
  const isPositiveChange = Number.isFinite(changeValue) && changeValue >= 0;

  const selectFavoritePair = (nextPair) => {
    switchFuturesPair({
      dispatch,
      setSearchParams,
      currentPair: pair,
      nextPair,
    });
  };

  return (
    <div className="futures-quote">
      <div className="futures-quote__pnl">
        <div className="futures-quote__pnl-copy">
          <p className="futures-quote__pnl-label">
            {__("futures.open_profit_loss")}
          </p>
          <p className="futures-quote__pnl-value">{formatOpenPnl(openPnl)}</p>
        </div>
        <button
          type="button"
          className="futures-quote__pnl-close"
          onClick={onCloseAllPositions || undefined}
          disabled={closeAllDisabled || !onCloseAllPositions}
        >
          {__("futures.close_all")}
        </button>
      </div>

      <div className="futures-quote__head">
        <FuturesPairs />

        <FavoritePairsShortcuts onSelect={selectFavoritePair} />
      </div>

      {tickerData && (
        <div
          className={`futures-quote__body${
            tickersLoading ? " futures-quote__body--loading" : ""
          }`}
        >
          <div className="futures-quote__stats-row">
            <div className="futures-quote__price-block">
              <p className="futures-quote__price">
                {formatQuotePrice(currentPrice)}
              </p>
              <div className="futures-quote__subprice-line">
                <p
                  className={`futures-quote__change ${
                    isPositiveChange
                      ? "futures-quote__change--up"
                      : "futures-quote__change--down"
                  }`}
                >
                  {formatChangePercent(tickerData.priceChangePercent)}
                </p>
                <p className="futures-quote__change-label">
                  <img src={clockIcon} alt="" />
                  <span className="futures-quote__change-text-full">
                    {__("futures.change_24h")}
                  </span>
                  <span className="futures-quote__change-text-short">24h</span>
                </p>
              </div>
            </div>

            <div className="futures-quote__range-block">
              <RangeStat
                direction="high"
                label={__("futures.high_24h")}
                value={tickerData.highPrice}
              />
              <RangeStat
                direction="low"
                label={__("futures.low_24h")}
                value={tickerData.lowPrice}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
